import FollowLink from '@/components/creative/FollowLink';
import AdminOnly from '@/components/ui/AdminOnly';
import { useCan } from '@/hooks/use-scopes';
import { humanize } from '@/lib/format';
import { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ExternalLink, Lock, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Card, PageHeader } from '@/components/ui/Card';
import PromptDialog from '@/components/ui/PromptDialog';
import Button from '@/components/ui/Button';
import { postPlaceLabel,
  qk, fetchCreative, fetchCreativeActivity, patchCreative, deferCreative, resumeCreative, fetchEditLock,
  type Creative } from '@/lib/queries';
import EditCreativeDialog from '@/components/creative/EditCreativeDialog';
import ActivityFeed from '@/components/activity/ActivityFeed';
import CollapsibleCard from '@/components/ui/CollapsibleCard';
import ChatBox from '@/components/creative/ChatBox';
import ShootCard from '@/components/creative/ShootCard';
import BoardCard from '@/components/creative/BoardCard';
import { TrackCard } from '@/components/creative/TrackBar';
import WorkCard from '@/components/creative/WorkCard';
import AnalyticsSection from '@/components/analytics/AnalyticsSection';
import ApproverRoutingCard from '@/components/creative/ApproverRouting';
import AssetsPanel from '@/components/creative/AssetsPanel';
import DoneOutsideDialog from '@/components/creative/DoneOutsideDialog';
import { useIsAdmin } from '@/hooks/use-scopes';
import RichView from '@/components/ui/RichView';
import BriefDocuments, { useBriefDocuments } from '@/components/creative/BriefDocuments';

export default function CreativeDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  // Moving the pipeline is planning: executors see the stepper read-only.
  const canSteer = useCan('creatives.write');
  const creative = useQuery({
    queryKey: qk.creative(id),
    queryFn: () => fetchCreative(id),
    enabled: !!id,
  });

  // Who may change the brief right now, and why not (api/app/edit_lock.py).
  const lock = useQuery({ queryKey: ['editLock', id], queryFn: () => fetchEditLock(id), enabled: !!id && canSteer });
  const [editing, setEditing] = useState(false);
  const [outside, setOutside] = useState(false);
  const isAdmin = useIsAdmin();

  const flipNeedsShoot = useMutation({
    mutationFn: async (v: boolean) => patchCreative(id, { needs_shoot: v }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.creative(id) }); qc.invalidateQueries({ queryKey: ['track', id] }); qc.invalidateQueries({ queryKey: ['creatives'] }); qc.invalidateQueries({ queryKey: ['shoot', id] }); },
  });

  //: parked while the world catches up, with a reason and a date when we have one
  const [deferring, setDeferring] = useState(false);
  const defer = useMutation({
    mutationFn: ({ reason, until, mentions }: { reason: string; until?: string; mentions?: string[] }) => deferCreative(id!, reason, until, mentions),
    onSuccess: () => { toast.success('On hold'); setDeferring(false); qc.invalidateQueries({ queryKey: qk.creative(id!) }); },
    onError: (e: unknown) => toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not put it on hold'),
  });
  const resume = useMutation({
    mutationFn: () => resumeCreative(id!),
    onSuccess: () => { toast.success('Back on'); qc.invalidateQueries({ queryKey: qk.creative(id!) }); },
    onError: (e: unknown) => toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not resume'),
  });
  const advance = useMutation({
    mutationFn: async (next: Creative['current_stage']) => patchCreative(id, { current_stage: next }),
    onSuccess: () => {
      toast.success('Stage updated');
      qc.invalidateQueries({ queryKey: qk.creative(id) });
      qc.invalidateQueries({ queryKey: ['editLock', id] });
      qc.invalidateQueries({ queryKey: qk.creativeActivity(id) });
    },
  });

  if (!id) return <Navigate to="/creatives" replace />;
  if (creative.isLoading) return <div className="text-muted text-sm">Loading...</div>;
  if (!creative.data) {
    // A link from a notification used to bounce silently to the list; say why instead.
    const status = (creative.error as { response?: { status?: number } } | null)?.response?.status;
    return (
      <Card className="max-w-md mx-auto mt-16 p-6 text-center">
        <h1 className="text-base font-semibold text-muted-strong mb-2">
          {status === 403 ? 'You are not on this creative' : status === 404 ? 'This creative no longer exists' : 'This creative did not load'}
        </h1>
        <p className="text-sm text-muted mb-4">
          {status === 403
            ? 'Ask the content creator or a manager to add you to it, then open the link again.'
            : status === 404 ? 'It may have been deleted.' : 'Check your connection and try again.'}
        </p>
        <div className="flex justify-center gap-2">
          {status !== 403 && status !== 404 && (<Button onClick={() => creative.refetch()}>Try again</Button>)}
          <Link to="/creatives"><Button variant="secondary">Go to creatives</Button></Link>
        </div>
      </Card>
    );
  }

  const c = creative.data;

  return (
    <div>
      <Link to="/creatives" className="inline-flex items-center gap-1 text-xs text-muted hover:text-accent-400 mb-3">
        <ArrowLeft size={12} /> Back to creatives
      </Link>
      <PageHeader
        title={c.title}
        subtitle={`${c.code} · ${c.client_name} · ${c.creative_type} · ${c.funnel_stage}`}
        actions={
          <div className="flex items-center gap-2">
            {canSteer && lock.data && (
              <Button variant="secondary" size="sm" onClick={() => setEditing(true)} data-testid="edit-open"
                icon={lock.data.locked ? <Lock size={12} /> : <Pencil size={12} />}
                title={lock.data.locked ? lock.data.message : lock.data.editable ? 'Edit the brief' : `The brief belongs to ${lock.data.owners.join(' and ') || 'its content creator'}`}>
                {lock.data.locked ? (lock.data.override ? 'Edit (locked)' : 'Locked') : 'Edit'}
              </Button>
            )}
            {canSteer && (c.deferred_at
              ? <Button variant="secondary" size="sm" onClick={() => resume.mutate()} loading={resume.isPending} data-testid="defer-resume">Back on</Button>
              : <Button variant="secondary" size="sm" onClick={() => setDeferring(true)} data-testid="defer-open">Put on hold</Button>)}
            {isAdmin && !['done', 'posted'].includes(c.current_stage) && (
              <Button variant="secondary" size="sm" onClick={() => setOutside(true)} data-testid="done-outside-open"
                title="Record work that was done outside the Warehouse and move on">Done outside</Button>
            )}
            <Link to={`/strategies/${c.strategy_id}`}>
              <Button variant="ghost" size="sm">View strategy</Button>
            </Link>
          </div>
        }
      />

      {/* A piece can be waiting on the world rather than on a person: the after is shot
          in six weeks. It stays visible and stops reading as late. */}
      {c.deferred_at && (
        <div className="mb-4 rounded-xl border border-amber-500/40 bg-amber-500/5 px-4 py-2.5 text-sm" data-testid="defer-banner">
          <span className="text-amber-300 font-medium">On hold.</span>{' '}
          <span className="text-muted-strong">{c.deferred_reason}</span>
          <span className="text-muted">{c.deferred_until ? ` Until ${new Date(c.deferred_until).toLocaleDateString('en-AE', { day: 'numeric', month: 'long', year: 'numeric' })}.` : ' No date yet.'}</span>
        </div>
      )}

      {outside && <DoneOutsideDialog creative={c} onClose={() => setOutside(false)} />}

      {c.client_request_id && (
        <div className="mb-4 rounded-xl border border-accent-400/40 bg-accent-600/10 px-4 py-3 text-sm text-muted-strong" data-testid="from-request">
          <b>From a client request.</b> The clinic's details, voice note and files are the source of this brief, and the
          conversation with them stays on the request. <Link to={`/requests/${c.client_request_id}`} className="text-accent-400 underline">Open the request</Link>
        </div>
      )}

      <TrackCard creative={c} canSteer={canSteer} pending={advance.isPending} onStage={(next) => advance.mutate(next)} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
        <CollapsibleCard title="Details" storageKey="creative.details.open">
          <Field label="Status">{humanize(c.status)}</Field>
          <Field label="Categories">{c.categories.length ? c.categories.join(', ') : <span className="text-muted">none</span>}</Field>
          <Field label="Channels">{c.channels.length ? c.channels.join(', ').replace(/_/g, ' ') : <span className="text-muted">none</span>}</Field>
          <Field label="Paid">{c.is_paid ? `yes (${humanize(c.ad_platforms.join(', ')) || '—'})` : 'no'}</Field>
          {['video', 'reel', 'photo'].includes(c.creative_type) && (
            <Field label="Shoot">
              <span data-testid="needs-shoot-value">{c.needs_shoot === false ? 'not needed, made from existing material' : 'needed'}</span>
              {canSteer && (
                <button type="button" onClick={() => flipNeedsShoot.mutate(!(c.needs_shoot !== false))} data-testid="needs-shoot-toggle"
                  className="ml-2 text-[11px] text-accent-400 hover:underline">
                  {c.needs_shoot === false ? 'needs a shoot after all' : 'no shoot, use existing material'}
                </button>
              )}
            </Field>
          )}
          <AdminOnly label="diagnostics" className="mt-3">
            <Field label="State">{c.state}</Field>
            <Field label="Revision round">{c.revision_round}</Field>
            <Field label="Client rounds">{c.client_adjustments_used ?? 0} used{c.client_adjustments_extra ? `, +${c.client_adjustments_extra} extra` : ''}</Field>
            <Field label="Client visible">{c.client_visible ? 'yes' : 'no'}</Field>
            <Field label="Id"><span className="font-mono text-[10px] break-all">{c.id}</span></Field>
          </AdminOnly>
        </CollapsibleCard>

        <CollapsibleCard title="Targeting" storageKey="creative.targeting.open">
          <Field label="Primary tag">{c.taxonomy_primary ?? <span className="text-muted">—</span>}</Field>
          <Field label="Secondary tag">{c.taxonomy_secondary ?? <span className="text-muted">—</span>}</Field>
          <Field label="Talent">{c.talent_name ?? <span className="text-muted">—</span>}</Field>
          <Field label="Execution date">{c.execution_date ?? <span className="text-muted">—</span>}</Field>
          <Field label="Posting date">{c.posting_date ?? <span className="text-muted">—</span>}</Field>
          <Field label="Posted">
            {(c.posted_links?.length ? c.posted_links : c.posted_url ? [{ platform: 'other', url: c.posted_url }] : []).length
              ? <span className="flex flex-col gap-0.5">{(c.posted_links?.length ? c.posted_links : [{ platform: 'other', url: c.posted_url! }]).map((l) => (
                  <a key={l.url} className="text-accent-400 underline" href={l.url} target="_blank" rel="noreferrer">{postPlaceLabel(l.platform)}</a>))}</span>
              : <span className="text-muted">—</span>}
          </Field>
        </CollapsibleCard>
      </div>

      {c.talent_note && (
        <CollapsibleCard
          className="mb-5 border-amber-500/30"
          headerClassName="[&>h3]:text-amber-300"
          title={`How ${c.talent_name ?? 'this doctor'} wants their work handled`}
          storageKey="creative.talentNote.open"
        >
          <p className="text-sm text-muted-strong whitespace-pre-wrap" dir="auto">{c.talent_note}</p>
        </CollapsibleCard>
      )}

      <BriefCard creative={c} />

      {/* Reference links from the brief. Rendered as anchors, so the server-side
          scheme allowlist (schemas._clean_reference_urls) is what keeps this safe. */}
      {!!c.reference_urls?.length && (
        <CollapsibleCard className="mb-5" title="References" storageKey="creative.references.open"
          badge={c.reference_urls.length}>
          <ul className="space-y-1.5">
            {c.reference_urls.map((u) => (
              <li key={u}>
                <a
                  href={u}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="group flex items-center gap-2 text-sm text-accent-400 hover:underline"
                  title={u}
                >
                  <ExternalLink size={13} className="shrink-0 opacity-70 group-hover:opacity-100" />
                  <span className="truncate">{u.replace(/^https?:\/\//, '')}</span>
                </a>
              </li>
            ))}
          </ul>
        </CollapsibleCard>
      )}

      <div id="shoot" className="mb-5 scroll-mt-4 rounded-xl transition-shadow">
        <ShootCard creative={c} />
      </div>
      <BoardCard creative={c} />
      <AnalyticsSection scope="creative" id={c.id} clientId={c.client_id}
        postedSince={c.posted_links?.map((l) => l.at).filter(Boolean).sort()[0] ?? c.posting_date} />

      <CollapsibleCard className="mb-5" title="Credits" storageKey="creative.credits.open"
        badge={Object.keys(c.credits).length || undefined}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
          {Object.entries(c.credits).map(([role, person]) => (
            <div key={role}>
              <div className="text-[10px] uppercase tracking-wide text-muted">{role.replace(/_/g, ' ')}</div>
              <div className="text-muted-strong">{person.name}</div>
            </div>
          ))}
          {Object.keys(c.credits).length === 0 && <div className="text-muted text-xs">No credits assigned yet.</div>}
        </div>
      </CollapsibleCard>

      <div id="assets" className="mb-5 scroll-mt-4 rounded-xl transition-shadow">
        <AssetsPanel creative={c} />
      </div>

      <div id="work" className="mb-5 scroll-mt-4 rounded-xl transition-shadow">
        <WorkCard creative={c} />
      </div>

      <div id="routing" className="scroll-mt-4">
        <ApproverRoutingCard creativeId={c.id} />
      </div>

      <div id="chat" className="mb-5 scroll-mt-4 rounded-xl transition-shadow">
        <ChatBox creative={c} />
      </div>
      <FollowLink />

      <ActivityFeed
        queryKey={qk.creativeActivity(id)}
        fetcher={() => fetchCreativeActivity(id)}
        storageKey="activityFeed.creative.open"
        emptyHint="No moves yet on this creative."
      />
      <EditCreativeDialog creative={c} lock={lock.data} open={editing} onClose={() => setEditing(false)} />
      <PromptDialog mentions open={deferring} title="Put this piece on hold"
        message="Say what it is waiting for. Add a date at the end if you have one, as 2026-11-15; the shoot is often not planned that far ahead, and no date is a real answer."
        placeholder="Waiting for the after, about six weeks" multiline maxLength={500} confirmLabel="Put on hold"
        onCancel={() => setDeferring(false)}
        onConfirm={(v, named) => {
          const text = v.trim();
          const m = text.match(/(\d{4}-\d{2}-\d{2})\s*$/);
          defer.mutate({ reason: (m ? text.slice(0, m.index).trim() : text) || text, until: m ? m[1] : undefined, mentions: named });
        }} />
    </div>
  );
}

/** The brief as written, and under it every document that came with it (Word, PDF,
 *  text), each opening in place. Shown when there is either. */
function BriefCard({ creative: c }: { creative: Creative }) {
  const { docs } = useBriefDocuments(c);
  if (!c.description && !docs.length) return null;
  return (
    // open on a first visit: Saad saw "Brief 3" closed and thought the work orders were missing (2026-11-01)
    <CollapsibleCard className="mb-5" title="Brief" storageKey="creative.brief.open" defaultOpen badge={docs.length || undefined}>
      {c.description
        ? <RichView html={c.description_html} plain={c.description} className="text-sm text-muted-strong" testid="brief-view" />
        : <div className="text-xs text-muted">No brief text; the brief is in the documents below.</div>}
      <BriefDocuments creative={c} />
    </CollapsibleCard>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-1.5 text-xs border-b border-slate-800/60 last:border-b-0">
      <div className="text-muted col-span-1">{label}</div>
      <div className="text-muted-strong col-span-2 truncate">{children}</div>

    </div>
  );
}
